import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendUp, TrendDown } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function VendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/vendors/${id}/analytics`).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-8 w-64 bg-muted rounded-sm" /><div className="h-64 bg-muted rounded-sm" /></div>;
  if (!data) return <div className="text-center py-12 text-[#475569]">Vendor not found</div>;

  const v = data.vendor;
  const monthlyData = data.monthly_trend?.map(m => ({ name: MONTHS[m.month - 1], amount: m.amount })) || [];

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="vendor-detail-page">
      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate(-1)} data-testid="back-btn">
        <ArrowLeft size={16} className="mr-1" /> Back
      </Button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{v.name}</h1>
          <p className="text-sm text-[#475569] mt-1">{v.vendor_code} &middot; {v.category} &middot; {v.status}</p>
        </div>
        {v.sap_vendor_code && (
          <span className="text-xs bg-[#DCFCE7] text-[#166534] px-2 py-1 rounded-sm font-mono">SAP: {v.sap_vendor_code}</span>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-sm border"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8]">Total Requests</p>
          <p className="text-2xl font-bold mt-1">{data.total_requests}</p>
        </CardContent></Card>
        <Card className="rounded-sm border"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8]">Current Year Spend</p>
          <p className="text-2xl font-bold mt-1">{fmt(data.current_year_spend)}</p>
        </CardContent></Card>
        <Card className="rounded-sm border"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8]">Last Year Spend</p>
          <p className="text-2xl font-bold mt-1">{fmt(data.last_year_spend)}</p>
        </CardContent></Card>
        <Card className="rounded-sm border"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8]">Change</p>
          <div className="flex items-center gap-1 mt-1">
            {data.pct_change >= 0 ? <TrendUp size={20} className="text-[#EF4444]" /> : <TrendDown size={20} className="text-[#22C55E]" />}
            <span className={`text-2xl font-bold ${data.pct_change >= 0 ? 'text-[#EF4444]' : 'text-[#22C55E]'}`}>{data.pct_change > 0 ? '+' : ''}{data.pct_change}%</span>
          </div>
        </CardContent></Card>
      </div>

      {/* Monthly Trend */}
      <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Monthly Spend Trend (Current Year)</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={monthlyData}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => fmt(v)} /><Bar dataKey="amount" fill="#0055FF" radius={[2, 2, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Vendor Details */}
        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Vendor Information</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Contact Person</span>{v.contact_person || '-'}</div>
            <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Email</span>{v.email || '-'}</div>
            <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Phone</span>{v.contact_number || '-'}</div>
            <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Address</span>{v.billing_address || '-'}</div>
            <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Tax ID</span>{v.tax_id || '-'}</div>
            <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Payment Terms</span>{v.payment_terms || '-'}</div>
          </CardContent>
        </Card>

        {/* Linked Contracts */}
        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Linked Contracts ({data.contracts?.length || 0})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Title</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">End Date</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.contracts?.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center text-[#94A3B8] py-4">No contracts</TableCell></TableRow>
                ) : (
                  data.contracts?.map(c => (
                    <TableRow key={c.id}><TableCell><Link to={`/contracts/${c.id}`} className="text-[#0055FF] text-sm">{c.contract_code}</Link></TableCell><TableCell className="text-sm">{c.title}</TableCell><TableCell className="text-sm">{c.end_date}</TableCell></TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
